import { SelectionModel } from '@angular/cdk/collections';

import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../shared/helpers/ordenacao-tabela.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { TruncatePipe } from '../../../shared/pipes/truncate.pipe';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import {
  ConfiguracaoColunasModalComponent,
  ColunaConfiguracao,
} from '../../../shared/components/configuracao-colunas-modal/configuracao-colunas-modal.component';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ServicoService } from '../../../shared/services/apis/servico/servico.service';
import { ServicoListaRequest } from '../../../shared/services/requests/servico/servico-lista.request';
import { ListaServicosResponse } from '../../../shared/services/responses/servico/lista-servicos.response';
import { ServicoListStruct } from '../../../shared/services/structs/servico/servico-list.struct';
import { ExcluirServicosMassaRequest } from '../../../shared/services/requests/servico/excluir-servicos-massa.request';

@Component({
  selector: 'app-servico-lista',
  standalone: true,
  imports: [
    HeaderComponent,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatSort,
    MatSortHeader,
    MatProgressSpinnerModule,
    MatTooltipModule,
    EmptyListComponent,
    RouterModule,
    LoadingBoxDefaultListComponent,
    TruncatePipe,
  ],
  templateUrl: './servico-lista.component.html',
  styleUrls: ['./servico-lista.component.css'],
})
export class ServicoListaComponent implements OnInit {
  // Enums
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Servicos;

  public model: FormGroup;
  public _isLoading: boolean = false;
  public _isLoadingAction: boolean = false;

  // Data and Pagination
  public _listaServicosResponse: ListaServicosResponse | undefined;
  public _dataSource = new DataSourceOrdenacaoInsensivel<ServicoListStruct>();
  public _paginaAtual: number = 1;
  public _ultimaPagina: boolean = false;

  public displayedColumns: string[] = [];

  // Columns Definition
  public todosCampos: ColunaConfiguracao[] = [
    { def: 'select', label: 'Seleção', visible: true, fixed: true, pinnedStart: true },
    { def: 'descricao', label: 'Descrição', visible: true, fixed: false },
    { def: 'codigo', label: 'Código', visible: true, fixed: false },
    { def: 'preco', label: 'Valor', visible: true, fixed: false },
    { def: 'isAtivo', label: 'Status', visible: true, fixed: false },
    { def: 'actions', label: 'Ações', visible: true, fixed: true, pinnedEnd: true },
  ];

  // Selection Control
  public selection = new SelectionModel<ServicoListStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private servicoService: ServicoService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.atualizarColunasVisiveis();
    this.pesquisar();
  }

  private atualizarColunasVisiveis(): void {
    this.displayedColumns = this.todosCampos.filter(c => c.visible).map(c => c.def);
  }

  public configurarColunas(): void {
    const dialogRef = this.dialog.open(ConfiguracaoColunasModalComponent, {
      maxWidth: '700px',
      data: { colunas: this.todosCampos },
    });

    dialogRef.afterClosed().subscribe((result: ColunaConfiguracao[]) => {
      if (result) {
        this.todosCampos = result;
        this.atualizarColunasVisiveis();
      }
    });
  }

  // --- Search and Pagination Logic ---

  public pesquisar(): void {
    if (this._isLoading) return;

    this._isLoading = true;

    const request = new ServicoListaRequest();
    request.pesquisa = this.model.get('pesquisa')?.value;

    this.servicoService.getAll(request, this._paginaAtual).subscribe({
      next: (response: ListaServicosResponse) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        if (!this._listaServicosResponse) {
          this._listaServicosResponse = response;
          this._dataSource.data = response.servicos;
        } else {
          this._listaServicosResponse.servicos.push(...response.servicos);
          this._dataSource.data = this._listaServicosResponse.servicos;
        }

        if (!response.servicos || response.servicos.length === 0 || !response.possuiProximaPagina) {
          this._ultimaPagina = true;
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message || err, AlertType.error);
      },
    });
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this._listaServicosResponse = undefined;
    this._dataSource.data = [];
    this._ultimaPagina = false;
    this.selection.clear();
    this.pesquisar();
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  // --- Selection Logic ---

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  toggleAllRows(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  checkboxLabel(row?: ServicoListStruct): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.idServico}`;
  }

  // --- Actions ---

  public abrirModalExcluir(servico?: ServicoListStruct): void {
    const isLote = !servico;
    const listaParaExcluir = isLote ? this.selection.selected : [servico!];

    if (listaParaExcluir.length === 0) {
      this.alertService.show('Atenção', 'Nenhum serviço selecionado.', AlertType.warning);
      return;
    }

    const descricao = isLote
      ? `Confirma a exclusão de ${listaParaExcluir.length} serviços selecionados?`
      : `Confirma a exclusão do serviço "${servico?.descricao}"?`;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmação de Exclusão',
        description: descricao,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.processarExclusao(listaParaExcluir);
      }
    });
  }

  private processarExclusao(lista: ServicoListStruct[]): void {
    this._isLoadingAction = true;
    const request = new ExcluirServicosMassaRequest();
    request.idsServico = lista.map(x => x.idServico);

    this.servicoService.excluirEmMassa(request).subscribe({
      next: res => {
        this._isLoadingAction = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Exclusão realizada.', AlertType.success);
        this.clickPesquisar();
      },
      error: err => {
        this._isLoadingAction = false;
        this.alertService.show('Erro', 'Falha ao excluir.', AlertType.error);
      },
    });
  }

  public formatarPreco(valor: number): string {
    if (valor == null) return 'R$ 0,00';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
}
